import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_CONVOY_APP, EnableConvoyAppInput } from '../data/mutations'

/**
 * Saves Convoy app's encoded api key with workspace ID and source.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const ConvoyModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableConvoyApp, { loading }] = useMutation<
    { enableConvoyApp: { enableAfterCreation: boolean; source: string } },
    EnableConvoyAppInput
  >(ENABLE_CONVOY_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Convoy app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Incorrect source') {
        setFormError('Invalid source. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid source. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { source: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableConvoyApp({
      variables: {
        appType,
        enableAfterCreation: true,
        source: event.source.toLowerCase() === 'convoy' ? 'Convoy' : event.source
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/apps/convoy"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable Convoy for Chaine`}
        subtitle={`Enabling this integration requires the source as Convoy.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'Convoy source',
            name: 'source',

            placeHolder: 'Enter the source as Convoy',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

ConvoyModal.displayName = 'ConvoyModal'

export { ConvoyModal }
